import { useState, useEffect, useRef } from 'react';

// Ticks down from the moment THIS component mounted, not from a server
// timestamp — comparing a server timestamp against the client's Date.now()
// breaks the moment a player's device clock is off (common on phones),
// either locking answering early or leaving it open past the real cutoff.
// A local, self-referential clock can't skew against itself.
export function CircularTimer({ limitSeconds, onExpire, size = 34 }: {
  limitSeconds: number;
  onExpire: () => void;
  size?: number;
}) {
  const [mountedAt] = useState(() => Date.now());
  const [now, setNow] = useState(mountedAt);
  const expiredRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  const elapsedSeconds = (now - mountedAt) / 1000;
  const remaining = Math.max(0, limitSeconds - elapsedSeconds);
  const fraction = limitSeconds > 0 ? remaining / limitSeconds : 0;

  useEffect(() => {
    if (remaining <= 0 && !expiredRef.current) {
      expiredRef.current = true;
      onExpire();
    }
  }, [remaining, onExpire]);

  const color = fraction < 0.25 ? '#FF4D6A' : fraction < 0.5 ? '#FFC93C' : '#39FF88';
  const stroke = Math.max(3, Math.round(size / 8.5));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;

  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="#2E2B45" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color}
          strokeWidth={stroke}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - fraction)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.1s linear, stroke 0.3s' }}
        />
      </svg>
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: size <= 34 ? '0.7rem' : '0.9rem',
        fontWeight: 700,
        color,
      }}>
        {Math.ceil(remaining)}
      </div>
    </div>
  );
}
